import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';

const ViewReceipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const fetchReceipts = async () => {
      try {
        const response = await axios.get('https://agpipefitting.org.et/api/get-receipts');
        setReceipts(response.data);
      } catch (error) {
        handleFetchError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchReceipts();
  }, []);

  const handleFetchError = (error) => {
    let errorText = 'An unexpected error occurred. Please try again later.';
    if (error.response) {
      errorText = error.response.data.message || 'Server error: Unable to fetch receipts';
    } else if (error.request) {
      errorText = 'Network error: Please check your internet connection';
    }
    setErrorMessage(errorText);
  };

  const handleDownload = async (id, receiptPath) => {
    try {
      setDownloadingId(id);
      const response = await axios.get(
        `https://agpipefitting.org.et/api/download?path=${encodeURIComponent(receiptPath)}`,
        { responseType: 'blob' }
      );
  
      console.log('Download response:', response);
  
      if (response.status !== 200) {
        throw new Error(`Failed with status: ${response.status}`);
      }
  
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'receipt.jpg';
  
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      } else {
        filename = receiptPath.split('/').pop();
      }
  
      saveAs(response.data, filename);
    } catch (error) {
      console.error('Download error:', error.response?.data || error.message);
      alert(error.response?.data?.error || 'Failed to download receipt');
    } finally {
      setDownloadingId(null);
    }
  };
  

  const formatDateTime = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return new Date(dateString).toLocaleString('en-US', options);
  };

  return (
    <div className="container-lg my-4">
      <header className="mb-5">
        <h1 className="h2 text-center fw-bold text-primary">Receipts Management</h1>
      </header>

      {loading ? (
        <div className="text-center py-5">
          <div 
            className="spinner-border text-primary" 
            style={{ width: '3rem', height: '3rem' }} 
            role="status"
          >
            <span className="visually-hidden">Loading receipts...</span>
          </div>
          <p className="mt-3 text-muted">Loading receipt data...</p>
        </div>
      ) : errorMessage ? (
        <div className="alert alert-danger mx-auto my-5 max-w-lg text-center" role="alert">
          <i className="bi bi-exclamation-circle me-2"></i>
          {errorMessage}
        </div>
      ) : (
        <div className="card shadow-sm border-0">
          <div className="card-body p-0">
            <div className="table-responsive rounded-3">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th scope="col" className="ps-4">Invoice #</th>
                    <th scope="col">Document</th>
                    <th scope="col">Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-5 text-muted">
                        No receipts found in the system
                      </td>
                    </tr>
                  ) : (
                    receipts.map((receipt) => (
                      <tr key={receipt.id}>
                        <td className="ps-4 fw-medium">{receipt.invoice_number}</td>
                        <td>
                          {receipt.receipt_path ? (
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleDownload(receipt.id, receipt.receipt_path)}
                              disabled={downloadingId === receipt.id}
                            >
                              {downloadingId === receipt.id ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-file-earmark-arrow-down me-2"></i>
                                  Download
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="text-muted fst-italic">Unavailable</span>
                          )}
                        </td>
                        <td>
                          <time dateTime={receipt.created_at}>
                            {formatDateTime(receipt.created_at)}
                          </time>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewReceipts;
